/**
 * LoanCarAllocationPanel
 * ──────────────────────
 * Drop into: src/components/bookings/LoanCarAllocationPanel.jsx
 *
 * Uses Redux (loanCarAllocationSlice) for all async state.
 * Local component state is limited to form field values only.
 *
 * PATCH INSTRUCTIONS for BookingsPage.jsx — at bottom of file.
 */

import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Car, Plus, Trash2, Edit2, X, Check, Loader2, AlertCircle, AlertTriangle, Calendar } from 'lucide-react';
import {
    fetchBookingAllocations,
    searchAvailableLoanCars,
    allocateLoanCar,
    updateLoanCarAllocation,
    removeLoanCarAllocation,
    openAllocationForm,
    closeAllocationForm,
    clearOperationStatus,
    evictBookingAllocations,
    selectAllocationsByBooking,
    selectAllocationLoadingForBooking,
    selectAvailableCars,
    selectAvailableCarsLoading,
    selectAvailableCarsError,
    selectShowAllocationForm,
    selectEditingAllocation,
    selectAllocationOperationLoading,
    selectAllocationOperationError,
} from '../store/slices/loanCarAllocationSlice';
import useEscapeToClose from './useEscapeToClose';

// ─────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────

const toInputValue = (dt) => (!dt ? '' : dt.length > 16 ? dt.substring(0, 16) : dt);

const formatDt = (dt) => {
    if (!dt) return '—';
    return new Date(dt).toLocaleString('en-AU', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const carLabel = (car) =>
    [car.year, car.make, car.model].filter(Boolean).join(' ') || car.rego;

const defaultStart = () => {
    const d = new Date(); d.setHours(8, 0, 0, 0);
    // Use local time, NOT toISOString — that converts to UTC and shifts the value.
    return localToInputValue(d);
};
const defaultEnd = () => {
    const d = new Date(); d.setDate(d.getDate() + 3); d.setHours(17, 0, 0, 0);
    return localToInputValue(d);
};

// Format a Date in the local timezone as `YYYY-MM-DDTHH:mm`
// (same shape as native <input type="datetime-local">).
const pad = (n) => String(n).padStart(2, '0');
const localToInputValue = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

// Split / join `YYYY-MM-DDTHH:mm` ⇄ { date, time }
const splitDateTime = (v) => {
    if (!v) return { date: '', time: '' };
    const [date = '', time = ''] = v.split('T');
    return { date, time: time.substring(0, 5) };
};
const joinDateTime = (date, time) => (date && time ? `${date}T${time}` : '');

const formatDtDisplay = (v) => {
    if (!v) return 'Select date & time';
    const d = new Date(v);
    if (isNaN(d.getTime())) return v;
    return d.toLocaleString('en-AU', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

// ─────────────────────────────────────────────────────────
// DateTimePickerField
// ─────────────────────────────────────────────────────────
//
// Replaces the native <input type="datetime-local"> control. The native
// picker has no OK/commit button on most platforms — on iOS Safari and
// some desktop browsers the dropdown closes as soon as the user touches
// outside it, so picking "date AND time" feels rigid and easy to commit
// half-finished.
//
// This component opens a small popover with separate date + time inputs,
// a Cancel button, and an explicit OK button. The parent's onChange is
// only fired when OK is pressed, so the value never half-updates.
//
// Output format is identical to <input type="datetime-local">:
//   "YYYY-MM-DDTHH:mm"
// so downstream code (handleSearch / payload / Redux thunks) is unchanged.
const DateTimePickerField = ({ value, onChange, ariaLabel }) => {
    const [open, setOpen] = useState(false);
    const [draftDate, setDraftDate] = useState('');
    const [draftTime, setDraftTime] = useState('');
    const wrapRef = useRef(null);

    // Seed draft from value whenever the popover opens
    const openPicker = () => {
        const { date, time } = splitDateTime(value);
        setDraftDate(date);
        setDraftTime(time || '08:00');
        setOpen(true);
    };

    const closePicker = () => setOpen(false);

    const commit = () => {
        if (!draftDate || !draftTime) return;
        onChange(joinDateTime(draftDate, draftTime));
        setOpen(false);
    };

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const onDocClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    // Close on Escape
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    return (
        <div className="relative" ref={wrapRef}>
            <button
                type="button"
                onClick={open ? closePicker : openPicker}
                aria-label={ariaLabel}
                className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-left flex items-center justify-between gap-2 focus:outline-none focus:ring-2 focus:ring-blue-400 hover:border-gray-300 transition-colors"
            >
                <span className={value ? 'text-gray-900' : 'text-gray-400'}>
                    {formatDtDisplay(value)}
                </span>
                <Calendar size={14} className="text-gray-400 shrink-0" />
            </button>

            {open && (
                <div className="absolute z-50 mt-1 left-0 w-64 bg-white border border-gray-200 rounded-xl shadow-lg p-3 space-y-3">
                    <div>
                        <label className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1">Date</label>
                        <input
                            type="date"
                            value={draftDate}
                            onChange={(e) => setDraftDate(e.target.value)}
                            className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                    </div>
                    <div>
                        <label className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1">Time</label>
                        <input
                            type="time"
                            value={draftTime}
                            onChange={(e) => setDraftTime(e.target.value)}
                            className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                    </div>
                    <div className="flex gap-2 pt-1">
                        <button
                            type="button"
                            onClick={closePicker}
                            className="flex-1 py-1.5 text-sm font-medium bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={commit}
                            disabled={!draftDate || !draftTime}
                            className="flex-1 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────
// AllocationForm
// ─────────────────────────────────────────────────────────

const AllocationForm = ({ bookingUuid }) => {
    const dispatch          = useDispatch();
    const editingAllocation = useSelector(selectEditingAllocation);
    const availableCars     = useSelector(selectAvailableCars);
    const searchLoading     = useSelector(selectAvailableCarsLoading);
    const searchError       = useSelector(selectAvailableCarsError);
    const saveLoading       = useSelector(selectAllocationOperationLoading);
    const saveError         = useSelector(selectAllocationOperationError);

    const isEdit = Boolean(editingAllocation);

    const [form, setForm] = useState({
        loanCarUuid:     editingAllocation?.loanCarUuid     ?? '',
        allocationStart: editingAllocation ? toInputValue(editingAllocation.allocationStart) : defaultStart(),
        allocationEnd:   editingAllocation ? toInputValue(editingAllocation.allocationEnd)   : defaultEnd(),
        notes:           editingAllocation?.notes           ?? '',
    });
    const [hasSearched, setHasSearched] = useState(false);
    const [localError, setLocalError]   = useState(null);

    // Auto-search when editing so the car list is pre-populated
    useEffect(() => {
        if (isEdit) handleSearch();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearch = () => {
        if (!form.allocationStart || !form.allocationEnd) return;
        if (form.allocationEnd <= form.allocationStart) {
            setLocalError('End must be after start');
            return;
        }
        setLocalError(null);
        setHasSearched(true);
        dispatch(searchAvailableLoanCars({
            start: form.allocationStart,
            end: form.allocationEnd,
            excludeAllocation: isEdit ? editingAllocation.uuid : null,
        }));
    };

    const handleWindowChange = (field, value) => {
        setForm(f => ({ ...f, [field]: value, loanCarUuid: '' }));
        setHasSearched(false);
    };

    const handleSave = () => {
        if (!form.loanCarUuid)     { setLocalError('Please select a loan car'); return; }
        if (!form.allocationStart) { setLocalError('Start datetime is required'); return; }
        if (!form.allocationEnd)   { setLocalError('End datetime is required'); return; }
        if (form.allocationEnd <= form.allocationStart) { setLocalError('End must be after start'); return; }
        setLocalError(null);

        const payload = {
            loanCarUuid:     form.loanCarUuid,
            allocationStart: form.allocationStart,
            allocationEnd:   form.allocationEnd,
            notes:           form.notes || null,
        };

        if (isEdit) {
            dispatch(updateLoanCarAllocation({
                bookingUuid,
                allocationUuid: editingAllocation.uuid,
                data: payload,
            }));
        } else {
            dispatch(allocateLoanCar({ bookingUuid, data: payload }));
        }
    };

    // When editing, ensure the current car always appears in the dropdown
    const carOptions = (isEdit && editingAllocation && !availableCars.find(c => c.uuid === editingAllocation.loanCarUuid))
        ? [{ uuid: editingAllocation.loanCarUuid, rego: editingAllocation.rego,
            make: editingAllocation.make, model: editingAllocation.model,
            year: editingAllocation.year }, ...availableCars]
        : availableCars;

    const displayError = localError || saveError;

    return (
        <div className="bg-blue-50 rounded-xl p-4 space-y-3">
            <h4 className="text-sm font-semibold text-blue-900">
                {isEdit ? 'Edit Loan Car Allocation' : 'Allocate a Loan Car'}
            </h4>

            <div className="grid grid-cols-2 gap-2">
                <div>
                    <label className="block text-xs text-gray-500 mb-1">Pick-up</label>
                    <DateTimePickerField
                        value={form.allocationStart}
                        onChange={(v) => handleWindowChange('allocationStart', v)}
                        ariaLabel="Pick-up date and time"
                    />
                </div>
                <div>
                    <label className="block text-xs text-gray-500 mb-1">Return</label>
                    <DateTimePickerField
                        value={form.allocationEnd}
                        onChange={(v) => handleWindowChange('allocationEnd', v)}
                        ariaLabel="Return date and time"
                    />
                </div>
            </div>

            <button
                onClick={handleSearch}
                disabled={searchLoading || !form.allocationStart || !form.allocationEnd}
                className="w-full py-2 text-sm font-medium bg-white border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-100 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
            >
                {searchLoading
                    ? <><Loader2 size={14} className="animate-spin" /> Searching…</>
                    : 'Check Availability'}
            </button>

            {searchError && (
                <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle size={12} /> {searchError}
                </p>
            )}

            {hasSearched && (
                carOptions.length === 0
                    ? <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                        No loan cars available for this window.
                    </p>
                    : (
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">Select Car</label>
                            <select
                                value={form.loanCarUuid}
                                onChange={e => setForm(f => ({ ...f, loanCarUuid: e.target.value }))}
                                className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                            >
                                <option value="">— choose —</option>
                                {carOptions.map(car => (
                                    <option key={car.uuid} value={car.uuid}>
                                        {carLabel(car)} · {car.rego}
                                        {car.baseCostPerDay ? ` · $${car.baseCostPerDay}/day` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )
            )}

            <div>
                <label className="block text-xs text-gray-500 mb-1">Notes (optional)</label>
                <textarea
                    value={form.notes}
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    rows={2}
                    placeholder="Fuel level, handover notes…"
                    className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                />
            </div>

            {displayError && (
                <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle size={12} /> {displayError}
                </p>
            )}

            <div className="flex gap-2">
                <button
                    onClick={handleSave}
                    disabled={saveLoading || !form.loanCarUuid}
                    className="flex-1 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-1 transition-colors"
                >
                    {saveLoading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    {isEdit ? 'Update' : 'Allocate'}
                </button>
                <button
                    onClick={() => dispatch(closeAllocationForm())}
                    className="px-4 py-2 text-sm font-medium bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    <X size={14} />
                </button>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────
// RemoveLoanCarConfirmDialog
// ─────────────────────────────────────────────────────────
//
// Replaces the previous `window.confirm()` used by AllocationRow when staff
// remove a loan car from a booking. The native browser dialog looked jarring
// on both desktop and tablet — it bypassed the app's visual language and on
// iPad was easy to dismiss accidentally — and on desktop didn't compose
// nicely with the Escape-to-close behaviour the rest of the modals adopted.
//
// This local dialog mirrors the variant: 'danger' style of the ConfirmDialog
// in JobDetailModal (rounded card, red icon + button) without depending on
// JobDetailModal — the consumer surface here is small enough that a
// dedicated local component is simpler than extracting and sharing. If a
// third caller comes along, lift this into a shared module.
//
// z-[60] sits above the host modal (BookingFormModal / BookingDetailModal
// both at z-50) so the confirm always renders on top of the panel that
// triggered it.
const RemoveLoanCarConfirmDialog = ({ open, rego, loading, onConfirm, onCancel }) => {
    // Desktop-only Escape to close. The shared stack means this confirm,
    // when open, captures Escape before the host modal — so Esc dismisses
    // just the confirm without collapsing the whole booking modal behind it.
    useEscapeToClose(open, onCancel);

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
            onClick={onCancel}
        >
            <div
                className="bg-white rounded-2xl w-full max-w-sm shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
            >
                <div className="px-5 py-4 border-b flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-red-100 text-red-600">
                        <AlertTriangle size={18} />
                    </div>
                    <h2 className="text-lg font-bold text-gray-900">Remove loan car?</h2>
                </div>
                <div className="px-5 py-4">
                    <p className="text-sm text-gray-600 leading-relaxed">
                        Remove loan car <span className="font-semibold text-gray-900">{rego}</span> from this booking?
                    </p>
                </div>
                <div className="px-5 py-3 border-t bg-gray-50 rounded-b-2xl flex gap-2 justify-end">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={loading}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={loading}
                        className="px-4 py-2 text-sm font-semibold rounded-lg transition-colors bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 flex items-center gap-2"
                    >
                        {loading && <Loader2 size={14} className="animate-spin" />}
                        Remove
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────
// AllocationRow
// ─────────────────────────────────────────────────────────

const AllocationRow = ({ allocation, bookingUuid }) => {
    const dispatch  = useDispatch();
    const opLoading = useSelector(selectAllocationOperationLoading);

    // Local-only confirm state — kept out of Redux because the dialog is
    // purely transient and scoped to a single row. The trash button flips
    // this to true; the dialog flips it back on cancel or fires the remove
    // thunk on confirm. opLoading flows from the slice, so during the
    // pending dispatch the dialog's button shows a spinner and stays open
    // until the row unmounts (success) or the user cancels.
    const [confirmOpen, setConfirmOpen] = useState(false);

    const handleRemove = () => setConfirmOpen(true);
    const handleConfirmRemove = () => {
        setConfirmOpen(false);
        dispatch(removeLoanCarAllocation({ bookingUuid, allocationUuid: allocation.uuid }));
    };

    return (
        <>
            <div className="flex items-start justify-between bg-gray-50 rounded-xl px-3 py-2 gap-2">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <Car size={14} className="text-emerald-600 shrink-0" />
                        <span className="font-semibold text-sm text-gray-900 truncate">
                            {carLabel(allocation)} · {allocation.rego}
                        </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 pl-5 space-y-0.5">
                        <div>Pick-up: {formatDt(allocation.allocationStart)}</div>
                        <div>Return:&nbsp; {formatDt(allocation.allocationEnd)}</div>
                        {allocation.notes && <div className="italic">{allocation.notes}</div>}
                    </div>
                </div>
                <div className="flex gap-1 shrink-0 mt-0.5">
                    <button
                        onClick={() => dispatch(openAllocationForm(allocation))}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                    >
                        <Edit2 size={14} />
                    </button>
                    <button
                        onClick={handleRemove}
                        disabled={opLoading}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Remove"
                    >
                        {opLoading ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    </button>
                </div>
            </div>

            <RemoveLoanCarConfirmDialog
                open={confirmOpen}
                rego={allocation.rego}
                loading={opLoading}
                onCancel={() => setConfirmOpen(false)}
                onConfirm={handleConfirmRemove}
            />
        </>
    );
};

// ─────────────────────────────────────────────────────────
// LoanCarAllocationPanel — public export
// ─────────────────────────────────────────────────────────

/**
 * Props:
 *   booking   — full booking object from Redux (needs .uuid and .status)
 *   readOnly  — hides add/edit/remove actions when true
 */
const LoanCarAllocationPanel = ({ booking, readOnly = false }) => {
    const dispatch   = useDispatch();
    const allocations = useSelector(state => selectAllocationsByBooking(state, booking.uuid));
    const { loading, error } = useSelector(state => selectAllocationLoadingForBooking(state, booking.uuid));
    const showForm   = useSelector(selectShowAllocationForm);

    const isClosed = booking.status === 'CANCELLED' || booking.status === 'COMPLETED';
    const canEdit  = !readOnly && !isClosed;

    useEffect(() => {
        dispatch(fetchBookingAllocations(booking.uuid));
        return () => {
            dispatch(evictBookingAllocations(booking.uuid));
            dispatch(clearOperationStatus());
        };
    }, [dispatch, booking.uuid]);

    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-500 flex items-center gap-1">
                    <Car size={14} />
                    Loan Car
                </h3>
                {canEdit && !showForm && (
                    <button
                        onClick={() => dispatch(openAllocationForm(null))}
                        className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
                    >
                        <Plus size={12} /> Allocate
                    </button>
                )}
            </div>

            {loading && (
                <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                    <Loader2 size={14} className="animate-spin" /> Loading…
                </div>
            )}

            {error && (
                <p className="text-xs text-red-600 flex items-center gap-1 py-1">
                    <AlertCircle size={12} /> {error}
                </p>
            )}

            {!loading && allocations.length === 0 && !showForm && (
                <p className="text-sm text-gray-400 italic py-1">No loan car allocated</p>
            )}

            {!loading && allocations.length > 0 && (
                <div className="space-y-2 mb-2">
                    {allocations.map(a => (
                        <AllocationRow key={a.uuid} allocation={a} bookingUuid={booking.uuid} />
                    ))}
                </div>
            )}

            {showForm && <AllocationForm bookingUuid={booking.uuid} />}
        </div>
    );
};

export default LoanCarAllocationPanel;